import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { FaIcon, faXmark, faCircleInfo, faGearSolid } from '@/lib/fa'

import { Button } from '@/ui/button'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useWrapConfirm } from '@/modules/shared/hooks/useWrapConfirm'
import { toast } from '@/lib/toasts/api'
import type {
  TimesheetModel,
  TimesheetRowModel,
} from '@/app/models/timesheetModel'
import { buildSettingLookup } from './explainSettingsLookup'
import { pickPageTarget } from './explainSettingFields'
import { buildExplainValueFormatter } from './explainValueFormatter'
import { useTabsStore } from '@/modules/shared/stores/tabsStore'
import { useExplainEditableSettings } from './useExplainEditableSettings'
import { EditableSettingField } from './EditableSettingField'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'

interface TimesheetExplainModalProps {
  isOpen: boolean
  onClose: () => void
  companyId: string
  rowKey: string | null
  timesheet: TimesheetModel | null
  onSettingsChanged?: () => void
}

export const TimesheetExplainModal: React.FC<TimesheetExplainModalProps> = ({
  isOpen,
  onClose,
  companyId,
  rowKey,
  timesheet,
  onSettingsChanged,
}) => {
  const { t, tSafe } = useLocalize()
  const navigate = useNavigate()
  const setTab = useTabsStore((s) => s.setTab)
  const wrapConfirm = useWrapConfirm()

  const editable = useExplainEditableSettings({
    companyId,
    enabled: isOpen,
  })

  const settingLookup = React.useMemo(
    () => buildSettingLookup(timesheet?.appliedSettings),
    [timesheet?.appliedSettings],
  )
  const formatValue = React.useMemo(() => buildExplainValueFormatter(t), [t])

  const row = React.useMemo<TimesheetRowModel | null>(() => {
    if (!rowKey || !timesheet?.rows) return null
    return timesheet.rows.find((r) => r.titleKey === rowKey) ?? null
  }, [rowKey, timesheet?.rows])

  React.useEffect(() => {
    if (!isOpen) editable.reset()
  }, [isOpen, rowKey])

  if (!isOpen || !rowKey) return null

  const ruleKeys = row?.appliedRuleKeys ?? []
  const rowTitle = tSafe(`explain.row.${rowKey}.title`, row?.title ?? rowKey)
  const rowDescription = tSafe(
    `explain.row.${rowKey}.description`,
    tSafe(
      'explain.row.fallback-description',
      "Calculated from the driver's activities and the company settings shown above.",
    ),
  )

  const target = pickPageTarget(ruleKeys)

  const handleOpenSettings = () => {
    setTab(target.scope, target.tabId)
    onClose()
    navigate(`/et/company/${companyId}/configuration/${target.page}`)
  }

  const doSave = async () => {
    try {
      await editable.save()
      toast.success({ text: t('configuration.data-updated') })
      onSettingsChanged?.()
    } catch (err) {
      console.error('Error saving settings inline:', err)
      toast.error({ text: tSafe('explain.modal.save-error', 'Failed to save settings') })
    }
  }

  const handleSave = wrapConfirm(doSave, {
    message: t('configuration.save-confirm'),
  })

  return (
    <ModalLayout >
      <div
        className="tw-fixed tw-inset-0 tw-z-[210] tw-flex tw-items-center tw-justify-center tw-bg-[#1f2937] tw-bg-opacity-60"
        onClick={onClose}
      >
        <div
          className="tw-flex tw-max-h-[80vh] tw-overflow-hidden tw-w-[560px] tw-max-w-[95vw] tw-flex-col tw-rounded-lg tw-bg-white tw-shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="tw-flex tw-h-[64px] tw-shrink-0 tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-px-6">
            <div className="tw-flex tw-items-center tw-gap-2">
              <FaIcon
                icon={faCircleInfo}
                className="tw-h-5 tw-w-5 tw-text-[#2448bb]"
              />
              <h2 className="tw-text-xl tw-font-semibold tw-leading-[28px] tw-text-[#1f2937]">
                {rowTitle}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded hover:tw-bg-gray-100"
              aria-label={t('common.close')}
            >
              <FaIcon
                icon={faXmark}
                className="tw-h-5 tw-w-5 tw-text-[#1f2937]"
              />
            </button>
          </div>

          <div className="tw-flex-1 tw-overflow-y-auto tw-px-6 tw-py-4">
            <p className="tw-mb-4 tw-text-xs tw-text-[#6b77a0]">
              {rowDescription}
            </p>

            <h3 className="tw-mb-3 tw-text-sm tw-font-semibold tw-text-[#1f2937]">
              {tSafe(
                'explain.row.applied-rules',
                'Settings that shape this row',
              )}
            </h3>

            {ruleKeys.length === 0 ? (
              <p className="tw-text-xs tw-text-[#6b77a0]">
                {tSafe(
                  'explain.modal.no-settings-for-row',
                  'No specific settings are tied to this row — it is computed directly from driver activities.',
                )}
              </p>
            ) : (
              <div className="tw-flex tw-flex-col tw-gap-4">
                {ruleKeys.map((key) => {
                  const setting = settingLookup.get(key)
                  return (
                    <EditableSettingField
                      key={key}
                      settingKey={key}
                      setting={setting}
                      value={editable.getValue(key, setting)}
                      onChange={(next) => editable.setValue(key, next)}
                      formatValue={formatValue}
                      tSafe={tSafe}
                      disabled={editable.isSaving || editable.isLoading}
                      readonlySuffix={editable.getReadonlySuffix(key)}
                    />
                  )
                })}
              </div>
            )}
          </div>

          <div className="tw-flex tw-h-[64px] tw-shrink-0 tw-items-center tw-justify-between tw-border-t tw-border-[#e5e6e6] tw-bg-white tw-px-6">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleOpenSettings}
              className="tw-flex tw-items-center tw-gap-2"
            >
              <FaIcon icon={faGearSolid} className="tw-h-4 tw-w-4" />
              {tSafe('explain.modal.open-settings', 'Open settings')}
            </Button>

            <div className="tw-flex tw-items-center tw-gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={editable.reset}
                disabled={!editable.isDirty || editable.isSaving}
              >
                {t('common.reset')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={!editable.isDirty || editable.isSaving}
              >
                {t('common.save')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ModalLayout>
  )
}

export default TimesheetExplainModal
